import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { BiArrowBack } from 'react-icons/bi';
import { MdEdit } from 'react-icons/md';

const BarrelBlanketDetails = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const [entry, setEntry] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchEntryDetails();
    }, [id]);

    const fetchEntryDetails = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem("apUserToken");

            if (!token) {
                alert('Authentication token not found. Please login again.');
                setLoading(false);
                return;
            }

            const response = await axios.get(
                `${process.env.REACT_APP_API_URL}barrelBlanket/${id}`,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );

            if (response.data.success) {
                setEntry(response.data.data);
            } else {
                alert(response.data.message || 'Failed to fetch entry details');
            }
        } catch (error) {
            console.error('API Error:', error);
            alert('Failed to fetch entry details. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderBarrelEntry = (barrelEntry, index) => (
        <div key={barrelEntry._id} className="bg-gray-100 p-5 rounded-lg border border-gray-300 mb-4">
            <h3 className="text-lg font-bold text-teal-600 mb-4">Barrel Entry #{index + 1}</h3>

            <div className="space-y-3">
                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Equipment ID:</span>
                    <span className="text-gray-800">{barrelEntry.equipmentId || 'N/A'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Model:</span>
                    <span className="text-gray-800">{barrelEntry.model || 'N/A'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Size:</span>
                    <span className="text-gray-800">{barrelEntry.size || 'N/A'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Setpoint Temp:</span>
                    <span className="text-gray-800">{barrelEntry.setpointTemp || 'N/A'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Orientation:</span>
                    <span className="text-gray-800">{barrelEntry.orientation || 'N/A'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Type of Cooling:</span>
                    <span className="text-gray-800">{barrelEntry.typeOfCooling || 'N/A'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Shrouding:</span>
                    <span className="text-gray-800">{barrelEntry.shrouding ? 'Yes' : 'No'}</span>
                </div>

                <div className="flex justify-between items-center">
                    <span className="font-semibold text-teal-600">Insulation:</span>
                    <span className="text-gray-800">{barrelEntry.insulation ? 'Yes' : 'No'}</span>
                </div>

                <div className="flex justify-between items-start">
                    <span className="font-semibold text-teal-600">Notes:</span>
                    <span className="text-gray-800 text-right">{barrelEntry.notes || 'N/A'}</span>
                </div>
            </div>

            {/* Equipment Sections */}
            {barrelEntry.equipmentSectionsSizes && barrelEntry.equipmentSectionsSizes.length > 0 && (
                <div className="mt-6 pt-6 border-t border-gray-300">
                    <h4 className="text-md font-bold text-teal-600 mb-4">Equipment Sections:</h4>
                    {barrelEntry.equipmentSectionsSizes.map((section, sectionIndex) => (
                        <div key={section._id} className="bg-blue-50 p-4 rounded-lg border border-teal-600 mb-3">
                            <h5 className="text-sm font-bold text-teal-600 mb-3">Section #{sectionIndex + 1}</h5>

                            <div className="space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-teal-600 text-sm">Circumference:</span>
                                    <span className="text-gray-800 text-sm">{section.circumference || 'N/A'}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-teal-600 text-sm">Width:</span>
                                    <span className="text-gray-800 text-sm">{section.width || 'N/A'}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-teal-600 text-sm">Surface Area:</span>
                                    <span className="text-gray-800 text-sm">{section.surfaceArea || 'N/A'}</span>
                                </div>

                                <div className="flex justify-between items-center">
                                    <span className="font-semibold text-teal-600 text-sm">Throw Blanket (TB):</span>
                                    <span className="text-gray-800 text-sm">{section.tb || 'N/A'}</span>
                                </div>

                                <div className="flex justify-between items-start">
                                    <span className="font-semibold text-teal-600 text-sm">Section Notes:</span>
                                    <span className="text-gray-800 text-sm text-right">{section.notes || 'N/A'}</span>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="bg-teal-600 text-white p-4 shadow-md">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="text-white hover:text-gray-200">
                            <BiArrowBack size={24} />
                        </button>
                        <h1 className="text-2xl font-bold">Barrel Blanket Details</h1>
                    </div>
                </div>
                <div className="flex items-center justify-center h-64">
                    <div className="text-center">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
                        <p className="text-gray-600">Loading details...</p>
                    </div>
                </div>
            </div>
        );
    }

    if (!entry) {
        return (
            <div className="min-h-screen bg-gray-100">
                <div className="bg-teal-600 text-white p-4 shadow-md">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="text-white hover:text-gray-200">
                            <BiArrowBack size={24} />
                        </button>
                        <h1 className="text-2xl font-bold">Barrel Blanket Details</h1>
                    </div>
                </div>
                <div className="flex flex-col items-center justify-center h-64">
                    <p className="text-lg font-bold text-gray-600">Entry not found</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-100">
            {/* Header */}
            <div className="bg-teal-600 text-white p-4 shadow-md">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <button onClick={() => navigate(-1)} className="text-white hover:text-gray-200">
                            <BiArrowBack size={24} />
                        </button>
                        <h1 className="text-2xl font-bold">Barrel Blanket Details</h1>
                    </div>
                    <button
                        onClick={() => navigate(`/barrel-blanket/edit/${id}`)}
                        className="text-white hover:text-gray-200"
                    >
                        <MdEdit size={24} />
                    </button>
                </div>
            </div>

            {/* Content */}
            <div className="container mx-auto p-4 max-w-4xl">
                {/* Main Entry Details */}
                <div className="bg-gray-100 p-5 rounded-lg border border-gray-300 mb-4">
                    <h2 className="text-lg font-bold text-teal-600 mb-4">Entry Information</h2>

                    <div className="space-y-3">
                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-teal-600">Company:</span>
                            <span className="text-gray-800">{entry.companyName || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-teal-600">City:</span>
                            <span className="text-gray-800">{entry.city || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-teal-600">State:</span>
                            <span className="text-gray-800">{entry.state || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-teal-600">Utility:</span>
                            <span className="text-gray-800">{entry.utility || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-teal-600">Username:</span>
                            <span className="text-gray-800">{entry.username || 'N/A'}</span>
                        </div>

                        <div className="flex justify-between items-center">
                            <span className="font-semibold text-teal-600">Date:</span>
                            <span className="text-gray-800">{new Date(entry.createdAt).toLocaleString()}</span>
                        </div>
                    </div>
                </div>

                {/* Barrel Entries */}
                {entry.barrelEntries && entry.barrelEntries.length > 0 && (
                    <div className="mb-4">
                        <h2 className="text-lg font-bold text-teal-600 mb-4">
                            Barrel Entries ({entry.barrelEntries.length})
                        </h2>
                        {entry.barrelEntries.map((barrelEntry, index) => renderBarrelEntry(barrelEntry, index))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default BarrelBlanketDetails;

